/**
 * MLB Headshot Service
 *
 * Provides programmatic access to MLB player headshots via the official CDN.
 * URL Pattern: https://img.mlbstatic.com/mlb-photos/image/upload/...
 *
 * @example
 * import { getMLBHeadshotUrl, MLB_PITCHER_REGISTRY } from './mlbHeadshotService';
 * const url = getMLBHeadshotUrl(669373); // Tarik Skubal
 */

// =============================================================================
// MLB HEADSHOT URL PATTERNS
// =============================================================================

/**
 * MLB CDN base URL for player photos
 */
const MLB_CDN_BASE = 'https://img.mlbstatic.com/mlb-photos/image/upload';

/**
 * Available headshot size presets
 */
export const HEADSHOT_SIZES = {
  SMALL: { width: 67, quality: 'auto:best' },    // Thumbnail
  MEDIUM: { width: 213, quality: 'auto:best' },  // Card view
  LARGE: { width: 426, quality: 'auto:best' },   // Profile page
  XLARGE: { width: 640, quality: 'auto:best' },  // Full report
};

/**
 * Headshot variants available
 */
export const HEADSHOT_VARIANTS = {
  CURRENT: 'current',     // Current season headshot
  SILO: 'silo/current',   // Transparent background
  ACTION: 'action',       // Action shot (if available)
};

// =============================================================================
// MLB PITCHER REGISTRY - Golden Source for MLB IDs
// =============================================================================

/**
 * Centralized registry of MLB pitchers with their IDs.
 * MLB ID (mlbId) is the authoritative identifier from Statcast/Baseball Savant.
 *
 * To add a new pitcher:
 * 1. Find their MLB ID on Baseball Savant: https://baseballsavant.mlb.com/
 * 2. Add entry with all metadata
 */
export const MLB_PITCHER_REGISTRY = {
  // Detroit Tigers Rotation
  skubal: {
    mlbId: 669373,
    name: 'Tarik Skubal',
    firstName: 'Tarik',
    lastName: 'Skubal',
    team: 'Detroit Tigers',
    teamId: 116,
    throws: 'L',
    position: 'SP',
    jersey: 29,
    birthDate: '1996-11-20',
    brefId: 'skubata01',        // Baseball-Reference ID
    fangraphsId: 22267,          // FanGraphs ID
    savantId: 669373,            // Same as mlbId
  },
  flaherty: {
    mlbId: 656427,
    name: 'Jack Flaherty',
    firstName: 'Jack',
    lastName: 'Flaherty',
    team: 'Detroit Tigers',
    teamId: 116,
    throws: 'R',
    position: 'SP',
    jersey: 22,
    birthDate: '1995-10-15',
    brefId: 'flaheja01',
    fangraphsId: 17479,
    savantId: 656427,
  },
  mize: {
    mlbId: 663554,
    name: 'Casey Mize',
    firstName: 'Casey',
    lastName: 'Mize',
    team: 'Detroit Tigers',
    teamId: 116,
    throws: 'R',
    position: 'SP',
    jersey: 12,
    birthDate: '1997-05-01',
    brefId: 'mizeca01',
    fangraphsId: 21856,
    savantId: 663554,
  },

  // Add more pitchers as needed...
  // Example template:
  // 'lastname': {
  //   mlbId: 000000,
  //   name: 'First Last',
  //   firstName: 'First',
  //   lastName: 'Last',
  //   team: 'Team Name',
  //   teamId: 000,
  //   throws: 'R|L',
  //   position: 'SP|RP',
  //   jersey: 00,
  //   birthDate: 'YYYY-MM-DD',
  //   brefId: 'lastfi01',
  //   fangraphsId: 00000,
  //   savantId: 000000,
  // },
};

// =============================================================================
// HEADSHOT URL GENERATORS
// =============================================================================

/**
 * Generate MLB headshot URL for a given player ID.
 *
 * MLB CDN has two reliable paths:
 * 1. Silo variant (transparent background): /v1/people/{mlbId}/headshot/silo/current
 * 2. Standard variant (size 67): /v1/people/{mlbId}/headshot/67/current
 *
 * We use the silo variant as default since it works consistently across all players
 * and allows width scaling via Cloudinary transforms.
 *
 * @param {number} mlbId - The MLB player ID (from Statcast/Baseball Savant)
 * @param {Object} options - Configuration options
 * @param {string} options.size - Size preset from HEADSHOT_SIZES (default: 'MEDIUM')
 * @param {string} options.variant - Variant from HEADSHOT_VARIANTS (default: 'SILO')
 * @returns {string} - Full URL to the player's headshot
 *
 * @example
 * getMLBHeadshotUrl(669373) // Skubal - default medium silo
 * getMLBHeadshotUrl(669373, { size: 'LARGE' })
 * getMLBHeadshotUrl(669373, { variant: 'CURRENT' }) // Non-transparent
 */
export function getMLBHeadshotUrl(mlbId, options = {}) {
  if (!mlbId) {
    console.warn('[MLBHeadshot] No mlbId provided');
    return getPlaceholderUrl(options);
  }

  const {
    size = 'MEDIUM',
    variant = 'SILO',  // Default to silo for most reliable results
    customWidth = null,
  } = options;

  const sizeConfig = HEADSHOT_SIZES[size] || HEADSHOT_SIZES.MEDIUM;
  const width = customWidth || sizeConfig.width;
  const quality = sizeConfig.quality;

  // Use silo variant by default (most reliable), or size 67 for standard
  const isSilo = variant === 'SILO';
  const pathSuffix = isSilo ? 'silo/current' : '67/current';

  // Fallback to generic headshot if player image not found
  const fallback = 'd_people:generic:headshot:silo:current.png';

  // MLB CDN URL: Cloudinary-style transforms + path
  return `${MLB_CDN_BASE}/w_${width},${fallback},q_${quality},f_auto/v1/people/${mlbId}/headshot/${pathSuffix}`;
}

/**
 * Get headshot URL by pitcher name (case-insensitive lookup).
 *
 * @param {string} pitcherName - Pitcher name or key (e.g., 'skubal', 'Skubal', 'Tarik Skubal')
 * @param {Object} options - Same as getMLBHeadshotUrl options
 * @returns {string} - Full URL to the player's headshot
 *
 * @example
 * getHeadshotByName('skubal')
 * getHeadshotByName('Tarik Skubal', { size: 'LARGE' })
 */
export function getHeadshotByName(pitcherName, options = {}) {
  const pitcher = lookupPitcher(pitcherName);
  if (!pitcher) {
    console.warn(`[MLBHeadshot] Pitcher not found: ${pitcherName}`);
    return getPlaceholderUrl(options);
  }
  return getMLBHeadshotUrl(pitcher.mlbId, options);
}

/**
 * Generate a placeholder URL when no headshot is available.
 */
export function getPlaceholderUrl(options = {}) {
  const { size = 'MEDIUM' } = options;
  const sizeNum = size === 'SMALL' ? 67 : size === 'LARGE' ? 426 : 213;
  return `${MLB_CDN_BASE}/w_${sizeNum},q_auto:best/v1/people/generic/headshot/${sizeNum}/current`;
}

// =============================================================================
// LOOKUP UTILITIES
// =============================================================================

/**
 * Look up pitcher info from registry.
 * Supports lookup by:
 * - Registry key (e.g., 'skubal')
 * - Full name (e.g., 'Tarik Skubal')
 * - Last name (e.g., 'Skubal')
 * - MLB ID (e.g., 669373)
 *
 * @param {string|number} identifier - Pitcher identifier
 * @returns {Object|null} - Pitcher info object or null if not found
 */
export function lookupPitcher(identifier) {
  if (!identifier) return null;

  // Direct key lookup (case-insensitive)
  if (typeof identifier === 'string') {
    const key = identifier.toLowerCase();
    if (MLB_PITCHER_REGISTRY[key]) {
      return MLB_PITCHER_REGISTRY[key];
    }

    // Search by name
    const found = Object.values(MLB_PITCHER_REGISTRY).find(p =>
      p.name.toLowerCase() === identifier.toLowerCase() ||
      p.lastName.toLowerCase() === identifier.toLowerCase() ||
      p.firstName.toLowerCase() === identifier.toLowerCase()
    );
    if (found) return found;
  }

  // Lookup by MLB ID
  if (typeof identifier === 'number') {
    return Object.values(MLB_PITCHER_REGISTRY).find(p => p.mlbId === identifier);
  }

  return null;
}

/**
 * Get MLB ID from pitcher name.
 *
 * @param {string} pitcherName - Pitcher name
 * @returns {number|null} - MLB ID or null
 */
export function getMLBIdByName(pitcherName) {
  const pitcher = lookupPitcher(pitcherName);
  return pitcher?.mlbId || null;
}

/**
 * Get all registered pitchers as an array.
 *
 * @returns {Array} - Array of pitcher info objects
 */
export function getAllPitchers() {
  return Object.entries(MLB_PITCHER_REGISTRY).map(([key, value]) => ({
    key,
    ...value,
  }));
}

/**
 * Check if a pitcher is registered.
 *
 * @param {string|number} identifier - Pitcher identifier
 * @returns {boolean}
 */
export function isPitcherRegistered(identifier) {
  return lookupPitcher(identifier) !== null;
}

// =============================================================================
// PRELOAD UTILITIES
// =============================================================================

/**
 * Preload headshot images for better UX.
 *
 * @param {Array<number>} mlbIds - Array of MLB IDs to preload
 * @param {Object} options - Headshot options
 */
export function preloadHeadshots(mlbIds, options = {}) {
  mlbIds.forEach(mlbId => {
    const img = new Image();
    img.src = getMLBHeadshotUrl(mlbId, options);
  });
}

/**
 * Preload all registered pitcher headshots.
 */
export function preloadAllRegisteredHeadshots(options = {}) {
  const mlbIds = Object.values(MLB_PITCHER_REGISTRY).map(p => p.mlbId);
  preloadHeadshots(mlbIds, options);
}

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  getMLBHeadshotUrl,
  getHeadshotByName,
  getPlaceholderUrl,
  lookupPitcher,
  getMLBIdByName,
  getAllPitchers,
  isPitcherRegistered,
  preloadHeadshots,
  preloadAllRegisteredHeadshots,
  HEADSHOT_SIZES,
  HEADSHOT_VARIANTS,
  MLB_PITCHER_REGISTRY,
};
